// Prices.jsx - Live spot prices for all four metals, the chart behind each one,
// and the two things a person can take away: a link to this exact view, and a
// line of HTML that puts the same chart on their own site.
//
// It is a genuinely useful reference page and it earns search traffic for the
// "gold price per gram today" family of queries, which is exactly the person who
// is about to sell something.
//
// The metal and the range live in the query string rather than in state alone,
// so a shared link opens on the chart the sender was actually looking at. That
// is the whole difference between a page somebody links to and one they
// describe.
//
// LEGEND
//   1. LiveChart   the chart, its switches, and what is written above it
//   2. ShareRow    copy the link, or go and get the embed
//   3. Prices      the page

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { useSpot, useAsync } from "../lib/hooks.js";
import * as analytics from "../lib/analytics.js";
import { publicApi } from "../lib/api.js";
import { siteOrigin } from "../lib/host.js";
import { money, moneyExact, dayTime, GRAMS_PER_DWT, METAL_LABELS } from "../lib/format.js";
import { METALS, RANGES, metalOf, rangeOf } from "../lib/chart.js";
import { PriceChart, Sparkline, Change } from "../ui/chart.jsx";
import { Panel, Alert, Loading, CopyButton, Skeleton } from "../ui/kit.jsx";
import { Refresh, ArrowRight, ArrowUpRight, Grid } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed, CountUp } from "../ui/motion.jsx";
import { CtaBand, MobileCta } from "./parts.jsx";
import { AlertForm } from "./StayUpdated.jsx";

const METAL_KEYS = METALS.map((metal) => metal.key);

////////// 1. THE CHART //////////

function LiveChart({ metal, range, onMetal, onRange }) {
  const chosen = metalOf(metal);
  const { data, loading, error } = useAsync(
    (signal) => publicApi.chart({ metals: metal, range }, signal),
    [metal, range]
  );

  const line = data?.series?.[0];
  // A range switch should not blank the chart. The old line stays until the new
  // one lands, which is the difference between a chart and a loading spinner.
  const points = line?.points || [];

  return (
    <section aria-labelledby="chart-heading">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div>
          <h2 id="chart-heading" className="eyebrow">{chosen.label} &middot; {chosen.symbol}/USD</h2>
          <p className="display display-xl text-ink mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            {line?.price ? <CountUp to={line.price} format={(value) => money(value)} /> : <span className="num">&mdash;</span>}
            <Change percent={line?.changePercent} amount={line?.change} showAmount className="text-base sm:text-lg" />
          </p>
          <p className="hint mt-1">
            per troy ounce &middot; {line?.perGram ? `${moneyExact(line.perGram)} per gram` : "—"}
            {data?.rangeLabel ? ` · change over ${data.rangeLabel}` : ""}
          </p>
        </div>

        <div className="seg" role="group" aria-label="Time range">
          {RANGES.map((option) => (
            <button
              key={option.key}
              type="button"
              className="seg-item num"
              aria-pressed={range === option.key}
              onClick={() => onRange(option.key)}
            >
              {option.short}
            </button>
          ))}
        </div>
      </div>

      {/* A fixed height, always. The chart measures itself into this box, so the
          page never moves when the data arrives or the range changes. */}
      <div className="mt-5 border border-rule bg-card px-2 pt-3 pb-1 text-ink">
        {error ? (
          <div className="grid place-items-center h-[240px] sm:h-[300px] lg:h-[360px]">
            <p className="hint">The price feed is not answering. The figures below are the last we had.</p>
          </div>
        ) : loading && !points.length ? (
          <div className="px-2 py-3">
            <Skeleton className="h-[228px] sm:h-[288px] lg:h-[348px] w-full" />
          </div>
        ) : (
          <PriceChart
            points={points}
            range={range}
            color={chosen.color}
            height={360}
            className="h-[240px] sm:h-[300px] lg:h-[360px]"
            label={`${chosen.label} price over ${data?.rangeLabel || range}`}
          />
        )}
      </div>

      {/* The other three metals, as a row you can switch to. Each carries its
          own shape, so the row is a summary of the market and not just a nav. */}
      <div className="grid gap-px bg-rule border border-rule border-t-0 sm:grid-cols-2 lg:grid-cols-4">
        {METALS.map((option) => (
          <MetalSwitch
            key={option.key}
            metal={option}
            range={range}
            active={option.key === metal}
            onSelect={() => onMetal(option.key)}
          />
        ))}
      </div>

      <p className="hint mt-3 leading-relaxed measure-wide">
        Spot is the open market price of pure metal. Readings inside a week are our own, taken every ten
        minutes. Anything longer is built from daily settlement data and scaled to spot, so the shape is
        the market&rsquo;s and the level is the one shown above.
      </p>
    </section>
  );
}

// One metal in the switcher row: name, price, change, and the week in miniature.
function MetalSwitch({ metal, range, active, onSelect }) {
  const { data } = useAsync(
    (signal) => publicApi.chart({ metals: metal.key, range, sparkline: true }, signal),
    [metal.key, range]
  );
  const line = data?.series?.[0];

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`text-left p-4 sm:p-5 transition-colors ${active ? "bg-bronze-wash" : "bg-paper hover:bg-surface"}`}
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="eyebrow">{metal.label}</span>
        <Change percent={line?.changePercent} className="text-[12.5px]" />
      </span>
      <span className="flex items-end justify-between gap-3 mt-2">
        <span className="display display-sm text-ink num">{line?.price ? money(line.price) : "—"}</span>
        <Sparkline points={line?.points || []} color={metal.color} width={72} height={24} />
      </span>
    </button>
  );
}

////////// 2. SHARE //////////
// Two different acts, deliberately next to each other. Sending somebody this
// view is one line; putting it on their own site is the other, and the second is
// the one that travels.

function ShareRow({ metal, range }) {
  const url = `${siteOrigin() || "https://internationalgoldandsilver.com"}/prices?metal=${metal}&range=${range}`;
  const text = `${metalOf(metal).label} price, live`;

  const shares = [
    { label: "X", href: `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { label: "Reddit", href: `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}` },
    { label: "Email", href: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}` },
  ];

  return (
    <div className="mt-8 flex flex-col gap-4 border-y border-rule py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="eyebrow">Share this view</span>
        <CopyButton value={url} label="Copy link" className="btn-quiet btn-sm" />
        {shares.map((share) => (
          <a
            key={share.label}
            href={share.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13.5px] text-muted hover:text-ink underline decoration-rule hover:decoration-bronze"
          >
            {share.label}
          </a>
        ))}
      </div>

      <Link to={`/embed?metal=${metal}&range=${range}`} className="btn btn-quiet btn-sm self-start sm:self-auto">
        <Grid size={15} /> Put this chart on your site
      </Link>
    </div>
  );
}

////////// 3. THE PAGE //////////

export default function Prices() {
  const { spot, loading } = useSpot({ refreshMs: 60_000 });

  // Once per visit. The page refreshes the spot price every minute and switching
  // metal or range replaces the URL, so anything keyed on either would count the
  // same visitor over and over.
  const seen = useRef(false);
  useEffect(() => {
    if (seen.current) return;
    seen.current = true;
    analytics.trackViewPrices();
  }, []);
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const [params, setParams] = useSearchParams();

  const metal = METAL_KEYS.includes(params.get("metal")) ? params.get("metal") : "gold";
  const range = RANGES.some((option) => option.key === params.get("range")) ? params.get("range") : "1d";

  // replace, not push, so switching ranges four times does not put four entries
  // in the back button between a visitor and the page they came from.
  const setView = useCallback((patch) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(patch)) next.set(key, value);
      return next;
    }, { replace: true });
  }, [setParams]);

  const onMetal = useCallback((value) => setView({ metal: value }), [setView]);
  const onRange = useCallback((value) => setView({ range: value }), [setView]);

  const title = useMemo(
    () => `Live ${metalOf(metal).label.toLowerCase()} price chart, ${rangeOf(range).label}`,
    [metal, range]
  );

  return (
    <>
      <Seo
        title="Live gold, silver and platinum prices"
        path="/prices"
        description="Live gold, silver, platinum and palladium charts in US dollars per troy ounce, per gram and per pennyweight, with a karat and purity reference table. Free to embed on any site."
        image={SITE.ogPrices}
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Metal prices", to: "/prices" }])} />

      <div className="shell py-10 sm:py-12 md:py-16">
        <Reveal as="p" from="none" className="eyebrow mb-4">Updated every minute</Reveal>
        <Reveal as="h1" delay={60} className="display display-xl text-ink max-w-3xl">Today&rsquo;s metal prices.</Reveal>
        <Reveal as="p" delay={130} className="lede mt-5 sm:mt-6 measure-wide text-muted">
          These are spot prices: the price of pure metal on the open market, in US dollars.
          Every offer anybody makes you, including ours, starts from these numbers.
        </Reveal>

        <div className="mt-9 sm:mt-12">
          <LiveChart metal={metal} range={range} onMetal={onMetal} onRange={onRange} />
        </div>

        <ShareRow metal={metal} range={range} />

        <p className="sr-only">{title}</p>

        {loading ? (
          <Loading label="Fetching prices" />
        ) : (
          <>
            <StaggerBoxed
              as="div"
              step={80}
              from="scale"
              className="grid gap-px bg-rule border border-rule sm:grid-cols-2 lg:grid-cols-4 mt-10 sm:mt-12"
              itemClassName="bg-paper h-full"
            >
              {METAL_KEYS.map((key) => (
                <div key={key} className="bg-paper p-5 sm:p-6 h-full">
                  <p className="eyebrow">{METAL_LABELS[key]}</p>
                  <p className="display display-md text-ink mt-2">
                    {spot?.perOunce?.[key]
                      ? <CountUp to={spot.perOunce[key]} format={(value) => money(value)} />
                      : <span className="num">&mdash;</span>}
                  </p>
                  <p className="hint">per troy ounce</p>
                  <dl className="mt-4 pt-4 border-t border-rule-soft space-y-1.5 text-[13.5px]">
                    <div className="flex justify-between">
                      <dt className="text-muted">Per gram</dt>
                      <dd className="num text-ink">{spot?.perGram?.[key] ? moneyExact(spot.perGram[key]) : "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted">Per pennyweight</dt>
                      <dd className="num text-ink">{spot?.perGram?.[key] ? moneyExact(spot.perGram[key] * GRAMS_PER_DWT) : "—"}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </StaggerBoxed>

            <p className="hint mt-3 flex items-center gap-2">
              <Refresh size={14} />
              {spot?.updatedAt ? `Last updated ${dayTime(spot.updatedAt)}` : "Waiting for the first reading"}
              {spot?.stale && " (the feed is not responding, treat this as approximate)"}
            </p>

            {spot?.stale && (
              <Alert tone="warning" className="mt-4 max-w-2xl">
                Our price feed is not responding at the moment, so these figures may be a few hours old.
                Call us for a current quote before you make a decision on them.
              </Alert>
            )}
          </>
        )}

        {/* The free watch, on the page where somebody has just finished looking
            at a number and wondering whether it will do that again. */}
        <section className="mt-14 md:mt-20 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:gap-12 items-start">
          <div>
            <Reveal as="h2" className="display display-lg text-ink">Do not sit and watch it.</Reveal>
            <p className="lede mt-4 measure-wide text-muted">
              We will send one message when {metalOf(metal).label.toLowerCase()} does something worth knowing about.
              It is free, there is no account, and one click stops it.
            </p>
            <p className="hint mt-5 leading-relaxed measure">
              Prefer the chart itself? The{" "}
              <Link to="/embed" className="underline decoration-bronze-soft hover:decoration-bronze">embed builder</Link>{" "}
              gives you a line of HTML for your own site. No key, no account, and you can turn our name off.
            </p>
          </div>
          <AlertForm metal={metal} source="prices" />
        </section>

        {/* Purity reference. The second half of the reason people land here. */}
        <section className="mt-14 md:mt-20">
          <Reveal as="h2" className="display display-lg text-ink">What a marking is worth</Reveal>
          <p className="lede mt-4 measure-wide text-muted">
            Karat gold and sterling silver are alloys. Only the precious metal fraction carries value.
            These are the standard fractions and what one gram of each is worth right now.
          </p>

          <div className="grid gap-8 lg:grid-cols-2 mt-9">
            {["gold", "silver"].map((key) => {
              const rows = settings?.purities?.[key] || [];
              return (
                <Panel key={key}>
                  <p className="eyebrow px-5 py-3.5 border-b border-rule">{METAL_LABELS[key]}</p>
                  <table className="stack-table w-full text-[14.5px]">
                    <thead>
                      <tr className="border-b border-rule-soft">
                        <th className="text-left eyebrow font-semibold px-5 py-2.5">Marking</th>
                        <th className="text-right eyebrow font-semibold px-3 py-2.5">Pure content</th>
                        <th className="text-right eyebrow font-semibold px-5 py-2.5">Per gram</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.value} className="border-b border-rule-soft last:border-0">
                          <td data-label="" className="px-5 py-3 text-ink font-medium">{row.label}</td>
                          <td data-label="Pure content" className="px-3 py-3 text-right num text-muted">{(row.factor * 100).toFixed(1)}%</td>
                          <td data-label="Per gram" className="px-5 py-3 text-right num text-ink">
                            {spot?.perGram?.[key] ? moneyExact(spot.perGram[key] * row.factor) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              );
            })}
          </div>

          <p className="hint mt-5 measure-wide leading-relaxed">
            The last column is the metal content of one gram of that alloy at today&rsquo;s spot price.
            It is not an offer, and no buyer pays it, because the metal still has to be refined and the
            buyer carries the price risk in between. Use it to compare two offers on the same footing.
          </p>

          <div className="flex flex-col xs:flex-row flex-wrap gap-3 mt-8">
            <Link to="/tools/gold-calculator" className="btn btn-outline">Work out a specific piece <ArrowRight size={16} /></Link>
            <Link to="/tools/karat-chart" className="btn btn-quiet">Karat and hallmark chart</Link>
            <Link to="/embed" className="btn btn-quiet">Free charts for your site <ArrowUpRight size={15} /></Link>
          </div>
        </section>
      </div>

      <MobileCta />

      <CtaBand
        title="Numbers are one thing. An offer is another."
        body="Send us the actual pieces in a free insured kit and we will weigh, test and quote every one of them, on camera."
      />
    </>
  );
}
